
import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../App';
import { MapPin, Globe, Users, CreditCard, PlayCircle, Music, HandHeart, Calendar, Video, ArrowRight, Settings, BarChart3, Edit, Trash2, Save, Plus, Heart, X, Clock, AlertTriangle, Camera, UploadCloud, Loader2, Link as LinkIcon, Briefcase, Utensils, Home, Check } from 'lucide-react';
import { Church, UserRole, PrayerGroup, Sermon, Cause, ChurchEvent } from '../types';
import { Link } from 'react-router-dom';
import { checkContentSafety } from '../services/geminiService';
import { supabase } from '../services/supabaseClient';
import SEO from './SEO';

interface Props {
  churchId: string;
}

const ChurchPage: React.FC<Props> = ({ churchId }) => {
  const { 
      churches, events, currentUser, prayerGroups, addPrayerGroup, joinPrayerGroup, leavePrayerGroup, updateChurch,
      sermons, addSermon, updateSermon, deleteSermon, causes, addCause, updateCause, deleteCause, addEvent, updateEvent, deleteEvent, t
  } = useAppContext();
  const church = churches.find(c => c.id === churchId);
  const churchEvents = events.filter(e => e.churchId === churchId);
  const churchCells = prayerGroups.filter(pg => pg.churchId === churchId);
  const churchSermons = sermons.filter(s => s.churchId === churchId);
  const churchCauses = causes.filter(c => c.churchId === churchId);
  
  const [activeTab, setActiveTab] = useState<'home' | 'sermons' | 'events' | 'cells' | 'causes' | 'give' | 'admin'>('home');
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showCreateCellModal, setShowCreateCellModal] = useState(false);
  
  // Creation States
  const [showSermonModal, setShowSermonModal] = useState(false);
  const [showCauseModal, setShowCauseModal] = useState(false);
  const [editingCauseId, setEditingCauseId] = useState<string | null>(null);
  
  // Sermon Edit State
  const [editingSermonId, setEditingSermonId] = useState<string | null>(null);

  // Event Management State
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [newEventData, setNewEventData] = useState({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      guestSpeakers: '',
      price: ''
  });
  
  const [newCellData, setNewCellData] = useState({ name: '', description: '', meetingSchedule: '' });
  const [newSermonData, setNewSermonData] = useState({ title: '', preacher: '', date: '', description: '', videoUrl: '' });
  const [newCauseData, setNewCauseData] = useState({ 
      title: '', 
      description: '', 
      type: 'OTHER' as any, 
      urgency: 'LOW' as any, 
      contactInfo: '',
      category: 'REQUEST' as 'REQUEST' | 'OFFER'
  });

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Image Upload State
  const [isUploading, setIsUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Settings State
  const [settingsData, setSettingsData] = useState({
      name: '',
      description: '',
      website: '',
      worshipSongs: [] as string[]
  });
  const [newSong, setNewSong] = useState('');

  // Sync settingsData with church prop when it changes
  useEffect(() => {
      if (church) {
          setSettingsData({
              name: church.name || '',
              description: church.description || '',
              website: church.website || '',
              worshipSongs: church.worshipSongs || []
          });
      }
  }, [church]);

  // Admin Check Logic
  const isGlobalAdmin = currentUser?.role === UserRole.ADMIN;
  
  // Only grant access if the user is explicitly a PASTOR and is associated with this church
  const isAuthorizedPastor = currentUser?.role === UserRole.PASTOR && (
      currentUser.churchId === churchId || 
      (church && church.admins.includes(currentUser.id))
  );
  
  const hasAdminAccess = isGlobalAdmin || isAuthorizedPastor;

  if (!church) return <div className="text-center p-10">Church not found</div>;

  const displayTabs = ['home', 'sermons', 'events', 'cells', 'causes', 'give'];
  if (hasAdminAccess) displayTabs.push('admin');

  // Client-side resizing helper
  const compressImage = (file: File, maxWidth: number): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = (maxWidth / width) * height;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);
                    
                    canvas.toBlob((blob) => {
                        if (blob) resolve(blob);
                        else reject(new Error("Canvas to Blob failed"));
                    }, 'image/jpeg', 0.8);
                };
            };
            reader.onerror = (error) => reject(error);
        });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'logoUrl' | 'bannerUrl') => {
        const file = e.target.files?.[0];
        if (!file || !church) return;
        
        setIsUploading(true);

        try {
            // 1. Resize image
            const maxWidth = field === 'logoUrl' ? 400 : 1200;
            const compressedBlob = await compressImage(file, maxWidth);
            
            // 2. Upload to Supabase Storage
            const fileExt = 'jpg';
            // Use church ID in path to organize files
            const fileName = `church/${church.id}/${field}-${Date.now()}.${fileExt}`;
            
            // Reuse 'avatars' bucket
            const { data, error } = await supabase.storage
                .from('avatars')
                .upload(fileName, compressedBlob, {
                    contentType: 'image/jpeg',
                    upsert: true
                });

            if (error) throw error;

            // 3. Get Public URL
            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(fileName);

            // 4. Update Church Data
            updateChurch({
                ...church,
                [field]: publicUrl
            });

        } catch (error: any) {
            console.error("Upload failed:", error);
            setError("Failed to upload image. Please try again.");
        } finally {
            setIsUploading(false);
        }
  };

  const handleCreateCell = async (e: React.FormEvent) => {
      e.preventDefault();
      if(!currentUser) return;
      setError(null);
      
      const safetyCheck = await checkContentSafety(`${newCellData.name} ${newCellData.description}`);
      if (!safetyCheck.safe) {
          setError(`Flagged: ${safetyCheck.reason}`);
          return;
      }

      const newGroup: PrayerGroup = {
          id: Date.now().toString(),
          churchId: church.id,
          name: newCellData.name,
          description: newCellData.description,
          meetingSchedule: newCellData.meetingSchedule,
          leaderId: currentUser.id,
          members: [currentUser.id]
      };
      
      addPrayerGroup(newGroup);
      setShowCreateCellModal(false);
      setNewCellData({ name: '', description: '', meetingSchedule: '' });
  };

  const handleSaveSermon = async (e: React.FormEvent) => {
      e.preventDefault();
      if(!currentUser) return;
      
      const safetyCheck = await checkContentSafety(`${newSermonData.title} ${newSermonData.description}`);
      if (!safetyCheck.safe) {
          setError(`Flagged: ${safetyCheck.reason}`);
          return;
      }

      const sermonPayload: Sermon = {
          id: editingSermonId || Date.now().toString(),
          churchId: church.id,
          title: newSermonData.title,
          preacher: newSermonData.preacher,
          date: newSermonData.date,
          description: newSermonData.description,
          videoUrl: newSermonData.videoUrl
      };

      if (editingSermonId) {
          await updateSermon(sermonPayload);
      } else {
          await addSermon(sermonPayload);
      }

      setShowSermonModal(false);
      setEditingSermonId(null);
      setNewSermonData({ title: '', preacher: '', date: '', description: '', videoUrl: '' });
  };

  const openSermonModal = (sermon?: Sermon) => {
      if (sermon) {
          setEditingSermonId(sermon.id);
          // Handle potential ISO string or timestamp number
          let dateStr = '';
          if (sermon.date) {
            const d = new Date(sermon.date);
            if (!isNaN(d.getTime())) {
                dateStr = d.toISOString().split('T')[0];
            }
          }
          
          setNewSermonData({
              title: sermon.title,
              preacher: sermon.preacher,
              date: dateStr,
              description: sermon.description || '',
              videoUrl: sermon.videoUrl || ''
          });
      } else {
          setEditingSermonId(null);
          setNewSermonData({ title: '', preacher: '', date: '', description: '', videoUrl: '' });
      }
      setShowSermonModal(true);
  };

  const openCauseModal = (cause?: Cause) => {
      if (cause) {
          setEditingCauseId(cause.id);
          setNewCauseData({
              title: cause.title,
              description: cause.description,
              type: cause.type as any,
              urgency: cause.urgency as any,
              contactInfo: cause.contactInfo,
              category: cause.category || 'REQUEST'
          });
      } else {
          setEditingCauseId(null);
          setNewCauseData({ title: '', description: '', type: 'OTHER', urgency: 'LOW', contactInfo: '', category: 'REQUEST' });
      }
      setShowCauseModal(true);
  };

  const handleCreateCause = async (e: React.FormEvent) => {
      e.preventDefault();
      if(!currentUser) return;
      
      const safetyCheck = await checkContentSafety(`${newCauseData.title} ${newCauseData.description}`);
      if (!safetyCheck.safe) {
          setError(`Flagged: ${safetyCheck.reason}`);
          return;
      }

      const causeData: Cause = {
          id: editingCauseId || Date.now().toString(),
          authorId: currentUser.id,
          churchId: church.id,
          title: newCauseData.title,
          description: newCauseData.description,
          type: newCauseData.type,
          urgency: newCauseData.urgency,
          contactInfo: newCauseData.contactInfo,
          category: newCauseData.category
      };

      if (editingCauseId) {
          updateCause(causeData);
      } else {
          await addCause(causeData);
      }
      
      setShowCauseModal(false);
      setEditingCauseId(null);
      setNewCauseData({ title: '', description: '', type: 'OTHER', urgency: 'LOW', contactInfo: '', category: 'REQUEST' });
  };

  // --- Event Management Functions ---
  const openEventModal = (event?: ChurchEvent) => {
      if (event) {
          setEditingEventId(event.id);
          const eventDate = new Date(event.date);
          setNewEventData({
              title: event.title,
              description: event.description || '',
              date: eventDate.toISOString().split('T')[0],
              time: eventDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false}),
              location: event.location,
              guestSpeakers: event.guestSpeakers ? event.guestSpeakers.join(', ') : '',
              price: event.price ? event.price.toString() : ''
          });
      } else {
          setEditingEventId(null);
          setNewEventData({ title: '', description: '', date: '', time: '', location: '', guestSpeakers: '', price: '' });
      }
      setShowEventModal(true);
  };

  const handleSaveEvent = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      const safetyCheck = await checkContentSafety(`${newEventData.title} ${newEventData.description}`);
      if (!safetyCheck.safe) {
          setError(`Event flagged: ${safetyCheck.reason}`);
          return;
      }

      const eventPayload: ChurchEvent = {
          id: editingEventId || Date.now().toString(),
          churchId: church.id,
          title: newEventData.title,
          description: newEventData.description,
          date: `${newEventData.date}T${newEventData.time}`,
          location: newEventData.location,
          guestSpeakers: newEventData.guestSpeakers.split(',').map(s => s.trim()).filter(s => s),
          attendeeCount: editingEventId ? (events.find(e => e.id === editingEventId)?.attendeeCount || 0) : 0,
          imageUrl: `https://picsum.photos/seed/${Date.now()}/800/400`,
          price: newEventData.price ? parseFloat(newEventData.price) : 0
      };

      if (editingEventId) {
          await updateEvent(eventPayload);
      } else {
          await addEvent(eventPayload);
      }
      
      setShowEventModal(false);
      setEditingEventId(null);
      setNewEventData({ title: '', description: '', date: '', time: '', location: '', guestSpeakers: '', price: '' });
  };

  const handleAddSong = () => {
      if(newSong.trim()) {
          setSettingsData(prev => ({ ...prev, worshipSongs: [...prev.worshipSongs, newSong.trim()] }));
          setNewSong('');
      }
  };

  const handleRemoveSong = (songToRemove: string) => {
      setSettingsData(prev => ({ ...prev, worshipSongs: prev.worshipSongs.filter(s => s !== songToRemove) }));
  };

  const handleSaveSettings = async () => {
      if(!church) return;
      setError(null);
      setSuccessMessage(null);

      const safetyCheck = await checkContentSafety(`${settingsData.name} ${settingsData.description}`);
      if (!safetyCheck.safe) {
          setError(`Settings flagged: ${safetyCheck.reason}`);
          return;
      }

      try {
          await updateChurch({
              ...church,
              name: settingsData.name,
              description: settingsData.description,
              website: settingsData.website,
              worshipSongs: settingsData.worshipSongs
          });
          setSuccessMessage("Church settings updated successfully!");
          setTimeout(() => setSuccessMessage(null), 3000);
      } catch (e: any) {
          setError("Failed to update settings: " + e.message);
      }
  };

  const getEmbedUrl = (url: string) => {
      if (!url) return '';
      if (url.includes('youtube.com/watch?v=')) {
          return url.replace('watch?v=', 'embed/');
      } else if (url.includes('youtu.be/')) {
          const id = url.split('youtu.be/')[1];
          return `https://www.youtube.com/embed/${id}`;
      }
      return url;
  };

  // Structured Data (Schema.org) for Church
  const churchSchema = {
    "@context": "https://schema.org",
    "@type": "Church",
    "name": church.name,
    "description": church.description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": church.address,
      "addressRegion": church.region,
      "addressCountry": church.country
    },
    "url": church.website,
    "image": church.logoUrl,
    "logo": church.logoUrl
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <SEO 
        title={`${church.name} - Holyness`}
        description={church.description}
        image={church.bannerUrl}
        path={`/church/${church.id}`}
        schema={churchSchema}
      />

      {/* Hidden File Inputs */}
      <input 
            type="file" 
            ref={logoInputRef} 
            className="hidden" 
            accept="image/*" 
            onChange={(e) => handleImageUpload(e, 'logoUrl')}
      />
      <input 
            type="file" 
            ref={bannerInputRef} 
            className="hidden" 
            accept="image/*" 
            onChange={(e) => handleImageUpload(e, 'bannerUrl')}
      />

      {/* Banner */}
      <div className="h-48 md:h-64 w-full bg-gray-300 relative group">
        <img src={church.bannerUrl} alt={`Banner for ${church.name}`} className="w-full h-full object-cover" />
        {hasAdminAccess && (
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                 <button 
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={isUploading}
                    className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-full hover:bg-white/30 transition font-bold flex items-center gap-2"
                >
                    {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
                    Change Banner
                </button>
            </div>
        )}
        <div className="absolute -bottom-10 left-6">
            <div className="relative group/logo w-24 h-24">
                <img src={church.logoUrl} alt={`${church.name} Logo`} className="w-24 h-24 rounded-lg border-4 border-white shadow-md bg-white object-cover" />
                {hasAdminAccess && (
                    <div 
                        onClick={() => !isUploading && logoInputRef.current?.click()}
                        className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center cursor-pointer opacity-0 group-hover/logo:opacity-100 transition"
                        title="Change Logo"
                    >
                         {isUploading ? <Loader2 className="w-8 h-8 text-white animate-spin" /> : <Camera className="w-8 h-8 text-white" />}
                    </div>
                )}
            </div>
        </div>
      </div>

      {/* Header Info */}
      <div className="pt-12 px-6 pb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">{church.name}</h1>
                <p className="text-gray-600 mt-1 max-w-xl">{church.description}</p>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {church.address}, {church.country}</span>
                    <a href={church.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline"><Globe className="w-4 h-4" /> {church.website}</a>
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {church.memberCount} {t('church_members')}</span>
                </div>
            </div>
            
            <div className="flex gap-2">
                {church.stripeConnected && (
                    <button 
                        onClick={() => setShowDonateModal(true)}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 shadow flex items-center gap-2"
                    >
                        <CreditCard className="w-4 h-4" /> {t('church_give')}
                    </button>
                )}
                {hasAdminAccess && (
                    <button 
                        onClick={() => setActiveTab('admin')}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 shadow-sm flex items-center gap-2"
                    >
                        <Settings className="w-4 h-4" /> {t('church_admin')}
                    </button>
                )}
            </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-t border-gray-200 px-6">
          <nav className="flex space-x-8 overflow-x-auto">
              {displayTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize whitespace-nowrap ${
                        activeTab === tab 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                      {t(`tab_${tab}`)}
                  </button>
              ))}
          </nav>
      </div>

      {/* Content */}
      <div className="p-6 bg-gray-50 min-h-[300px]">
          {activeTab === 'home' && (
              <div className="space-y-6">
                  {/* Member Count Highlight */}
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
                      <div>
                          <h3 className="text-lg font-bold text-gray-900">Our Church Family</h3>
                          <p className="text-gray-500 text-sm mt-1">United in faith and service.</p>
                      </div>
                      <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
                          <Users className="w-5 h-5 text-blue-600" />
                          <span className="text-xl font-bold text-blue-900">{church.memberCount}</span>
                          <span className="text-sm font-medium text-blue-700">{t('church_members')}</span>
                      </div>
                  </div>

                  {/* Worship Section */}
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 relative group">
                      <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                              <Music className="w-5 h-5 text-purple-600"/> {t('worship_songs')}
                          </h3>
                          {hasAdminAccess && (
                              <button onClick={() => setActiveTab('admin')} className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 hover:bg-gray-200">Manage</button>
                          )}
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-purple-900 mb-3 font-medium">Songs we are singing this season:</p>
                          <div className="flex flex-wrap gap-2">
                              {church.worshipSongs.map(song => (
                                  <span key={song} className="bg-white text-purple-700 border border-purple-200 px-3 py-1 rounded-full text-sm font-medium shadow-sm flex items-center gap-1 cursor-pointer hover:bg-purple-100 transition">
                                      <PlayCircle className="w-3 h-3"/> {song}
                                  </span>
                              ))}
                              {church.worshipSongs.length === 0 && <span className="text-sm text-gray-500 italic">No songs listed currently.</span>}
                          </div>
                      </div>
                  </div>

                  {/* Community Work Highlights */}
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <HandHeart className="w-5 h-5 text-pink-600"/> {t('community_impact')}
                      </h3>
                      {churchCauses.length > 0 ? (
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {churchCauses.slice(0, 4).map(cause => (
                                  <div key={cause.id} className="relative group overflow-hidden rounded-lg shadow-sm border border-gray-100 bg-white p-4">
                                      <div className="flex items-start justify-between">
                                          <div className="p-2 bg-orange-50 rounded-full">
                                            {cause.type === 'FOOD' && <Utensils className="w-5 h-5 text-orange-500"/>}
                                            {cause.type === 'HOUSING' && <Home className="w-5 h-5 text-orange-500"/>}
                                            {cause.type === 'JOB' && <Briefcase className="w-5 h-5 text-orange-500"/>}
                                            {cause.type === 'OTHER' && <AlertTriangle className="w-5 h-5 text-orange-500"/>}
                                          </div>
                                          {cause.urgency === 'HIGH' && <span className="bg-red-100 text-red-600 text-[10px] font-bold px-2 py-1 rounded">{t('urgent')}</span>}
                                      </div>
                                      <h4 className="font-bold text-gray-900 mt-2">{cause.title}</h4>
                                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{cause.description}</p>
                                  </div>
                              ))}
                           </div>
                      ) : (
                          <div className="text-center py-6 bg-gray-50 border border-dashed rounded-lg">
                              <p className="text-gray-500">No community causes listed yet.</p>
                          </div>
                      )}
                  </div>
              </div>
          )}

          {activeTab === 'sermons' && (
              <div className="space-y-6">
                  {hasAdminAccess && (
                      <div className="flex justify-end">
                          <button 
                              onClick={() => openSermonModal()}
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2 text-sm"
                          >
                              <Plus className="w-4 h-4" /> {t('post_sermon')}
                          </button>
                      </div>
                  )}

                  <div className="grid gap-6">
                      {churchSermons.length > 0 ? churchSermons.map(sermon => (
                          <div key={sermon.id} className="flex flex-col md:flex-row bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden relative group">
                              <div className="md:w-64 h-48 bg-gray-900 flex-shrink-0 relative group/video">
                                  {/* Embed Video or Fallback */}
                                  {sermon.videoUrl ? (
                                    <iframe 
                                        src={getEmbedUrl(sermon.videoUrl)} 
                                        className="w-full h-full" 
                                        frameBorder="0" 
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                        allowFullScreen
                                        title={sermon.title}
                                    ></iframe>
                                  ) : (
                                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                          <Video className="w-12 h-12 mb-2 opacity-30" />
                                          <span className="text-xs font-medium opacity-60">{t('sermon_no_video')}</span>
                                      </div>
                                  )}
                                  
                                  {/* Overlay for Adding/Editing Video if Admin */}
                                  {hasAdminAccess && !sermon.videoUrl && (
                                       <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover/video:opacity-100 transition cursor-pointer" onClick={() => openSermonModal(sermon)}>
                                            <button className="bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 hover:bg-blue-700">
                                                <Plus className="w-3 h-3" /> {t('add_video')}
                                            </button>
                                       </div>
                                  )}
                              </div>
                              <div className="p-4 flex-1 relative">
                                  <div className="flex justify-between items-start">
                                      <h3 className="text-lg font-bold text-gray-900 line-clamp-2">{sermon.title}</h3>
                                      {hasAdminAccess && (
                                          <div className="flex gap-1 p-1 shrink-0">
                                              <button 
                                                  onClick={() => openSermonModal(sermon)} 
                                                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition" 
                                                  title="Edit Details"
                                              >
                                                  <Edit className="w-4 h-4"/>
                                              </button>
                                              <button 
                                                  onClick={() => { if(window.confirm('Delete this sermon?')) deleteSermon(sermon.id); }} 
                                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition" 
                                                  title="Delete Sermon"
                                              >
                                                  <Trash2 className="w-4 h-4"/>
                                              </button>
                                          </div>
                                      )}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-500 font-medium mb-3 mt-1">
                                      <span className="flex items-center gap-1"><Users className="w-3 h-3"/> {sermon.preacher}</span>
                                      <span className="text-gray-300">•</span>
                                      <span className="flex items-center gap-1"><Clock className="w-3 h-3"/> {new Date(sermon.date).toLocaleDateString()}</span>
                                  </div>
                                  {/* RENDER DESCRIPTION AS HTML */}
                                  <div 
                                      className="text-gray-600 text-sm line-clamp-3 prose prose-sm max-w-none"
                                      dangerouslySetInnerHTML={{ __html: sermon.description }}
                                  />
                              </div>
                          </div>
                      )) : (
                          <div className="text-center py-10 bg-gray-50 border border-dashed rounded-lg text-gray-500">
                              No sermons available yet.
                          </div>
                      )}
                  </div>
              </div>
          )}

          {activeTab === 'events' && (
              <div className="space-y-6">
                  <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-bold text-gray-900">{t('upcoming_gatherings')}</h2>
                      <div className="flex items-center gap-2">
                          <Link to="/events" className="text-blue-600 font-medium text-sm hover:underline flex items-center gap-1">
                              View All Events <ArrowRight className="w-4 h-4" />
                          </Link>
                          {hasAdminAccess && (
                              <button 
                                  onClick={() => openEventModal()}
                                  className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-700 flex items-center gap-1"
                              >
                                  <Plus className="w-3 h-3" /> Add Event
                              </button>
                          )}
                      </div>
                  </div>
                  <div className="grid gap-4">
                      {churchEvents.length > 0 ? churchEvents.map(event => (
                          <div key={event.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex gap-4">
                              <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-lg flex flex-col items-center justify-center text-blue-700">
                                  <span className="text-xs font-bold uppercase">{new Date(event.date).toLocaleString('default', { month: 'short' })}</span>
                                  <span className="text-xl font-bold">{new Date(event.date).getDate()}</span>
                              </div>
                              <div className="flex-1">
                                  <div className="flex justify-between items-start">
                                      <h4 className="font-bold text-gray-900">{event.title}</h4>
                                      {hasAdminAccess && (
                                          <div className="flex gap-1">
                                              <button onClick={() => openEventModal(event)} className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Edit className="w-4 h-4"/></button>
                                              <button onClick={() => { if(window.confirm('Delete event?')) deleteEvent(event.id); }} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4"/></button>
                                          </div>
                                      )}
                                  </div>
                                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                      <Clock className="w-3 h-3"/> {new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      <span className="mx-1">•</span>
                                      <MapPin className="w-3 h-3"/> {event.location}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-2">{event.description}</p>
                              </div>
                          </div>
                      )) : (
                          <div className="text-center py-8 bg-gray-50 border border-dashed rounded-lg">
                              <p className="text-gray-500 italic">No upcoming events scheduled.</p>
                          </div>
                      )}
                  </div>
              </div>
          )}

          {activeTab === 'cells' && (
              <div className="space-y-6">
                  <div className="flex justify-between items-center">
                      <div>
                          <h2 className="text-lg font-bold text-gray-900">{t('prayer_cells')}</h2>
                          <p className="text-sm text-gray-500">Join a group to connect deeper.</p>
                      </div>
                      {hasAdminAccess && (
                          <button 
                              onClick={() => setShowCreateCellModal(true)}
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-blue-700 flex items-center gap-2"
                          >
                              <Plus className="w-4 h-4" /> {t('create_cell')}
                          </button>
                      )}
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                      {churchCells.map(group => {
                          const isMember = group.members?.includes(currentUser?.id || '');
                          return (
                            <div key={group.id} className={`border rounded-lg p-5 transition hover:shadow-sm ${isMember ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                                <h3 className="font-bold text-gray-900">{group.name}</h3>
                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 mb-2">
                                    <Clock className="w-3 h-3" /> {group.meetingSchedule}
                                </div>
                                <p className="text-sm text-gray-600 mb-4">{group.description}</p>
                                <div className="flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-500 bg-white border px-2 py-1 rounded-full">{group.members?.length || 0} Members</span>
                                    {isMember ? (
                                        <button 
                                            onClick={() => leavePrayerGroup(group.id)}
                                            className="text-red-600 hover:text-red-700 text-xs font-bold px-3 py-1 rounded hover:bg-red-50 border border-transparent hover:border-red-100 transition"
                                        >
                                            {t('leave_group')}
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => joinPrayerGroup(group.id)}
                                            className="text-blue-600 hover:text-blue-700 text-xs font-bold px-3 py-1 rounded hover:bg-blue-50 border border-blue-100 transition"
                                        >
                                            {t('join_group')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )})}
                  </div>
              </div>
          )}

          {activeTab === 'causes' && (
               <div className="space-y-6">
                  <div className="flex justify-between items-center">
                      <div>
                          <h2 className="text-lg font-bold text-gray-900">{t('acts_of_service')}</h2>
                          <p className="text-sm text-gray-500">Opportunities to serve our local community.</p>
                      </div>
                      {hasAdminAccess && (
                          <button 
                              onClick={() => openCauseModal()}
                              className="bg-orange-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-orange-700 flex items-center gap-2"
                          >
                              <Plus className="w-4 h-4" /> Add Cause
                          </button>
                      )}
                  </div>

                  <div className="grid gap-4">
                      {churchCauses.map(cause => (
                          <div key={cause.id} className="bg-white p-4 rounded-lg shadow-sm border border-l-4 border-orange-400">
                              <div className="flex justify-between items-start">
                                  <div>
                                      <div className="flex items-center gap-2">
                                          <h3 className="font-bold text-gray-900">{cause.title}</h3>
                                          <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase ${cause.category === 'REQUEST' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                              {cause.category === 'REQUEST' ? 'Need' : 'Offer'}
                                          </span>
                                      </div>
                                      <p className="text-sm text-gray-600 mt-2">{cause.description}</p>
                                  </div>
                                  {hasAdminAccess && (
                                      <div className="flex gap-1 ml-4">
                                          <button 
                                              onClick={() => openCauseModal(cause)}
                                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                              title="Edit"
                                          >
                                              <Edit className="w-4 h-4" />
                                          </button>
                                          <button 
                                              onClick={() => {
                                                  if(window.confirm('Are you sure you want to delete this cause?')) {
                                                      deleteCause(cause.id);
                                                  }
                                              }}
                                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                              title="Delete"
                                          >
                                              <Trash2 className="w-4 h-4" />
                                          </button>
                                      </div>
                                  )}
                              </div>
                              <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                                  <span className="font-bold bg-gray-100 px-2 py-1 rounded">Contact: {cause.contactInfo}</span>
                                  {cause.urgency === 'HIGH' && <span className="text-red-600 font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Urgent</span>}
                              </div>
                          </div>
                      ))}
                      {churchCauses.length === 0 && (
                          <div className="text-center py-10 bg-gray-50 border border-dashed rounded-lg text-gray-500">
                              No active causes listed.
                          </div>
                      )}
                  </div>
               </div>
          )}

          {activeTab === 'give' && (
              <div className="text-center py-12">
                  <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{t('support_ministry')}</h2>
                  <p className="text-gray-600 max-w-md mx-auto mt-2 mb-6">
                      Your tithes and offerings help us spread the Gospel and support our community projects.
                  </p>
                  <button 
                    onClick={() => setShowDonateModal(true)}
                    className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold shadow-lg hover:bg-green-700 transition"
                  >
                      {t('give_securely')}
                  </button>
              </div>
          )}

          {activeTab === 'admin' && hasAdminAccess && (
              <div className="space-y-8 animate-fadeIn">
                  {/* Church Settings */}
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Settings className="w-5 h-5 text-gray-500" /> Church Settings
                      </h3>
                      
                      {successMessage && (
                          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                              <Check className="w-5 h-5" /> {successMessage}
                          </div>
                      )}
                      {error && (
                           <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                              <AlertTriangle className="w-5 h-5" /> {error}
                          </div>
                      )}

                      <div className="grid gap-4">
                          <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Church Name</label>
                              <input 
                                  type="text" 
                                  className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                  value={settingsData.name}
                                  onChange={e => setSettingsData({...settingsData, name: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                              <textarea 
                                  className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors h-24 resize-none"
                                  value={settingsData.description}
                                  onChange={e => setSettingsData({...settingsData, description: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                              <input 
                                  type="text" 
                                  className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                  value={settingsData.website}
                                  onChange={e => setSettingsData({...settingsData, website: e.target.value})}
                              />
                          </div>

                          {/* Worship Songs Management */}
                          <div className="border-t border-gray-100 pt-4 mt-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><Music className="w-4 h-4"/> Worship Songs Management</label>
                              <div className="flex gap-2 mb-3">
                                  <input 
                                      type="text" 
                                      className="flex-1 bg-transparent border-b border-gray-300 rounded-none px-2 py-1 text-sm focus:outline-none focus:border-purple-600"
                                      placeholder="Add a new song..."
                                      value={newSong}
                                      onChange={e => setNewSong(e.target.value)}
                                      onKeyDown={e => e.key === 'Enter' && handleAddSong()}
                                  />
                                  <button onClick={handleAddSong} className="bg-purple-100 text-purple-700 px-3 py-1 rounded text-xs font-bold hover:bg-purple-200">Add</button>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                  {settingsData.worshipSongs.map((song, idx) => (
                                      <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs flex items-center gap-1 group">
                                          {song}
                                          <button onClick={() => handleRemoveSong(song)} className="text-gray-400 hover:text-red-500"><X className="w-3 h-3"/></button>
                                      </span>
                                  ))}
                                  {settingsData.worshipSongs.length === 0 && <span className="text-xs text-gray-400 italic">No songs listed.</span>}
                              </div>
                          </div>

                          <div className="flex justify-end pt-4">
                              <button 
                                  onClick={handleSaveSettings}
                                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2"
                              >
                                  <Save className="w-4 h-4" /> Save Changes
                              </button>
                          </div>
                      </div>
                  </div>

                  {/* Community Impact Management */}
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                              <HandHeart className="w-5 h-5 text-orange-600" /> Community Impact Management
                          </h3>
                          <button 
                              onClick={() => openCauseModal()}
                              className="bg-orange-50 text-orange-700 border border-orange-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-orange-100 flex items-center gap-1"
                          >
                              <Plus className="w-3 h-3" /> New Project
                          </button>
                      </div>
                      
                      <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 border-b border-gray-100">
                                  <tr>
                                      <th className="text-left py-2 px-3 font-medium text-gray-500">Title</th>
                                      <th className="text-left py-2 px-3 font-medium text-gray-500">Type</th>
                                      <th className="text-left py-2 px-3 font-medium text-gray-500">Urgency</th>
                                      <th className="text-right py-2 px-3 font-medium text-gray-500">Actions</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {churchCauses.map(cause => (
                                      <tr key={cause.id} className="border-b border-gray-50 hover:bg-gray-50">
                                          <td className="py-2 px-3 font-medium text-gray-900">{cause.title}</td>
                                          <td className="py-2 px-3 text-gray-500">{cause.type}</td>
                                          <td className="py-2 px-3">
                                              <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${cause.urgency === 'HIGH' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                                  {cause.urgency}
                                              </span>
                                          </td>
                                          <td className="py-2 px-3 text-right flex justify-end gap-1">
                                              <button 
                                                  onClick={() => openCauseModal(cause)}
                                                  className="text-gray-400 hover:text-blue-600 p-1"
                                                  title="Edit"
                                              >
                                                  <Edit className="w-4 h-4" />
                                              </button>
                                              <button 
                                                  onClick={() => {
                                                      if(window.confirm('Delete this community cause?')) {
                                                          deleteCause(cause.id);
                                                      }
                                                  }}
                                                  className="text-gray-400 hover:text-red-600 p-1"
                                                  title="Delete"
                                              >
                                                  <Trash2 className="w-4 h-4" />
                                              </button>
                                          </td>
                                      </tr>
                                  ))}
                                  {churchCauses.length === 0 && (
                                      <tr><td colSpan={4} className="text-center py-4 text-gray-400 italic">No community projects listed.</td></tr>
                                  )}
                              </tbody>
                          </table>
                      </div>
                  </div>

                  {/* Team Management Shortcut */}
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                              <Users className="w-5 h-5 text-gray-500" /> Pastoral Team
                          </h3>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">
                          Add other pastors or leaders to help manage this church page.
                      </p>
                      <Link 
                          to="/admin/users"
                          className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-bold hover:bg-gray-200"
                      >
                          <Settings className="w-4 h-4" /> Go to Team Management
                      </Link>
                  </div>
              </div>
          )}
      </div>

      {/* MODALS */}
      
      {/* Create Cell Modal */}
      {showCreateCellModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                      <h3 className="font-bold text-lg">Create Prayer Cell</h3>
                      <button onClick={() => setShowCreateCellModal(false)}><X className="text-gray-400 hover:text-gray-600"/></button>
                  </div>
                  {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                  <form onSubmit={handleCreateCell} className="space-y-4">
                      <input 
                          type="text" placeholder="Cell Name" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newCellData.name} onChange={e => setNewCellData({...newCellData, name: e.target.value})}
                      />
                      <input 
                          type="text" placeholder="Schedule (e.g. Tuesdays 7PM)" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newCellData.meetingSchedule} onChange={e => setNewCellData({...newCellData, meetingSchedule: e.target.value})}
                      />
                      <textarea 
                          placeholder="Description" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600 h-20 resize-none"
                          value={newCellData.description} onChange={e => setNewCellData({...newCellData, description: e.target.value})}
                      />
                      <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold">Create Cell</button>
                  </form>
              </div>
          </div>
      )}

      {/* Add/Edit Sermon Modal */}
      {showSermonModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                      <h3 className="font-bold text-lg">{editingSermonId ? 'Edit Sermon' : 'Add Sermon'}</h3>
                      <button onClick={() => setShowSermonModal(false)}><X className="text-gray-400 hover:text-gray-600"/></button>
                  </div>
                  {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                  <form onSubmit={handleSaveSermon} className="space-y-4">
                      <input 
                          type="text" placeholder="Sermon Title" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newSermonData.title} onChange={e => setNewSermonData({...newSermonData, title: e.target.value})}
                      />
                      <input 
                          type="text" placeholder="Preacher Name" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newSermonData.preacher} onChange={e => setNewSermonData({...newSermonData, preacher: e.target.value})}
                      />
                      <input 
                          type="date" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newSermonData.date} onChange={e => setNewSermonData({...newSermonData, date: e.target.value})}
                      />
                      <input 
                          type="url" placeholder="Video URL (YouTube/Vimeo)"
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newSermonData.videoUrl} onChange={e => setNewSermonData({...newSermonData, videoUrl: e.target.value})}
                      />
                      <textarea 
                          placeholder="Description / Key Points"
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600 h-20 resize-none"
                          value={newSermonData.description} onChange={e => setNewSermonData({...newSermonData, description: e.target.value})}
                      />
                      <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold">
                          {editingSermonId ? 'Update Sermon' : 'Post Sermon'}
                      </button>
                  </form>
              </div>
          </div>
      )}

      {/* Add/Edit Cause Modal */}
      {showCauseModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                      <h3 className="font-bold text-lg">{editingCauseId ? 'Edit Cause' : 'Add Community Cause'}</h3>
                      <button onClick={() => setShowCauseModal(false)}><X className="text-gray-400 hover:text-gray-600"/></button>
                  </div>
                  {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                  <form onSubmit={handleCreateCause} className="space-y-4">
                      <input 
                          type="text" placeholder="Title (e.g. Food Drive)" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newCauseData.title} onChange={e => setNewCauseData({...newCauseData, title: e.target.value})}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <select 
                            className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                            value={newCauseData.type} onChange={e => setNewCauseData({...newCauseData, type: e.target.value as any})}
                        >
                            <option value="FOOD">Food</option>
                            <option value="HOUSING">Housing</option>
                            <option value="JOB">Job</option>
                            <option value="OTHER">Other</option>
                        </select>
                        <select 
                            className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                            value={newCauseData.urgency} onChange={e => setNewCauseData({...newCauseData, urgency: e.target.value as any})}
                        >
                            <option value="LOW">Low Urgency</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="HIGH">High</option>
                        </select>
                      </div>
                      
                      <select 
                            className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                            value={newCauseData.category} onChange={e => setNewCauseData({...newCauseData, category: e.target.value as any})}
                      >
                            <option value="REQUEST">This is a Need (Request)</option>
                            <option value="OFFER">This is an Offer (Service)</option>
                      </select>

                      <input 
                          type="text" placeholder="Contact Info" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600"
                          value={newCauseData.contactInfo} onChange={e => setNewCauseData({...newCauseData, contactInfo: e.target.value})}
                      />
                      <textarea 
                          placeholder="Description" required
                          className="w-full border-b border-gray-300 p-2 focus:outline-none focus:border-blue-600 h-20 resize-none"
                          value={newCauseData.description} onChange={e => setNewCauseData({...newCauseData, description: e.target.value})}
                      />
                      <button type="submit" className="w-full bg-orange-600 text-white py-2 rounded-lg font-bold hover:bg-orange-700">
                          {editingCauseId ? 'Update Cause' : 'Post Cause'}
                      </button>
                  </form>
              </div>
          </div>
      )}

      {/* Donate Modal */}
      {showDonateModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-fadeIn overflow-hidden">
                  <div className="bg-green-600 p-6 text-white text-center">
                      <Heart className="w-12 h-12 mx-auto mb-2 text-green-100" />
                      <h3 className="text-xl font-bold">Give to {church.name}</h3>
                      <p className="text-green-100 text-sm">Secure Tithes & Offerings</p>
                  </div>
                  <div className="p-6">
                      <p className="text-gray-600 text-center mb-6 text-sm">
                          Select an amount to give securely via Stripe.
                      </p>
                      <div className="grid grid-cols-3 gap-3 mb-6">
                          <button className="py-2 border rounded hover:bg-green-50 hover:border-green-500 font-bold text-gray-700">$10</button>
                          <button className="py-2 border rounded hover:bg-green-50 hover:border-green-500 font-bold text-gray-700">$50</button>
                          <button className="py-2 border rounded hover:bg-green-50 hover:border-green-500 font-bold text-gray-700">$100</button>
                      </div>
                      <input type="number" placeholder="Other Amount" className="w-full border p-3 rounded mb-4" />
                      <button 
                        onClick={() => { alert("Redirecting to Stripe..."); setShowDonateModal(false); }}
                        className="w-full bg-green-600 text-white py-3 rounded-lg font-bold shadow hover:bg-green-700 transition"
                      >
                          Proceed to Payment
                      </button>
                      <button onClick={() => setShowDonateModal(false)} className="w-full mt-3 text-gray-500 text-sm hover:underline">Cancel</button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
};

export default ChurchPage;
